import { useState, useCallback, useEffect, useRef } from 'react';
import { soaBuilderAPI } from '../../../services/api';

const STEP_DEFS = {
  clientDetails:          { label: 'Client Details',           icon: 'bi-person' },
  scope:                  { label: 'Scope of Advice',          icon: 'bi-file-earmark-text' },
  currentSituation:       { label: 'Current Situation',        icon: 'bi-wallet2' },
  objectives:             { label: 'Objectives & Goals',       icon: 'bi-bullseye' },
  riskProfile:            { label: 'Risk Profile',             icon: 'bi-shield-check' },
  strategy:               { label: 'Recommended Strategy',     icon: 'bi-graph-up-arrow' },
  retirementProjection:   { label: 'Retirement Projection',    icon: 'bi-graph-up' },
  productComparison:      { label: 'Product Comparison',       icon: 'bi-arrow-left-right' },
  fees:                   { label: 'Fees & Costs',             icon: 'bi-cash-stack' },
  actionsAuthority:       { label: 'Actions & Authority',      icon: 'bi-list-check' },
  warnings:               { label: 'Warnings & Disclaimers',   icon: 'bi-exclamation-triangle' },
};

const STEP_ORDER = [
  'clientDetails', 'scope', 'currentSituation', 'objectives',
  'riskProfile', 'strategy', 'retirementProjection', 'productComparison', 'fees',
  'actionsAuthority', 'warnings',
];

const DEFAULT_FORM_DATA = {
  clientDetails: {
    fullName: '',
    dateOfBirth: '',
    address: '',
    email: '',
    phone: '',
    employment: '',
    employer: '',
    isCouple: false,
    homeowner: 'Yes',
    partnerName: '',
    partnerDob: '',
    partnerEmployment: '',
    dependants: '',
  },
  scope: {
    adviceCovered: '',
    adviceExcluded: '',
    limitations: '',
  },
  currentSituation: {
    assets: [],
    liabilities: [],
    superannuation: '',
    insurance: '',
    currentPortfolio: '',
    notes: '',
    incomeItems: [],
    expenditureItems: [],
  },
  objectives: {
    retirementAge: '',
    incomeNeeds: '',
    capitalGrowth: '',
    timeHorizon: '',
    specificGoals: '',
    notes: '',
  },
  riskProfile: {
    selectedProfile: '',
    description: '',
    suitabilityStatement: '',
  },
  strategy: {
    portfolioCode: '',
    rationale: '',
    implementationNotes: '',
    blendMode: 'blend',
    modelWeights: {},
    fundEntries: [],
    assetClassWeights: {},
    blendResults: null,
  },
  retirementProjection: {
    couple: null,
    homeowner: 'Yes',
    birth_year_1: null,
    birth_year_2: null,
    retirement_age: null,
    financial_assets: 0,
    non_financial_assets: 0,
    investment_property_value: 0,
    employment_income: 0,
    other_income: 0,
    net_rental_income: 0,
    work_years: 0,
    other_income_years: 0,
    rental_income_years: 0,
    employment_entire_retirement: false,
    other_income_entire_retirement: false,
    rental_entire_retirement: false,
    income_stream: 0,
    income_stream_asset_test: 0,
    income_stream_income_test: 0,
    annuity_commencement_age: 67,
    annuity_type: 'inflation_adjusted',
    annuity_reference_portfolio: '',
    accelerated_reduction_rate: 0,
    current_portfolio_growth_exposure: 0,
    drawdown_method: '$',
    drawdown_input: 75000,
    use_sis_minimum: false,
    desired_bequest: 0,
    years: 30,
    blended_tax_rate: 0,
    annual_contributions: 0,
    lump_sum_withdrawals: [],
    step_down_withdrawals: [],
    results: null,
    frozenScenarios: [],
    savedScenarioId: null,
  },
  productComparison: {
    currentProduct: '',
    currentProductFees: '',
    currentProductFeatures: '',
    proposedProductFeatures: '',
    replacementReason: '',
    comparisons: [],
  },
  fees: {
    adviserFee: '',
    platformFee: '',
    portfolioIcr: '',
    investmentAmount: '',
    implementationFee: '',
    feeNotes: '',
    currentFees: { adviserFee: '', platformFee: '', icr: '' },
    feeBenefitStatement: '',
  },
  actionsAuthority: {
    actionItems: [],
    authorityText: '',
    specificAuthorisations: [],
  },
  warnings: {
    specificWarnings: '',
    productRisks: '',
    alternativesConsidered: '',
    customDisclaimer: '',
    nextSteps: '',
    adviserName: '',
    adviserEmail: '',
    adviserPhone: '',
    afslNumber: '',
    authorisedRepNumber: '',
    conflictsDisclosure: '',
    adviserRemuneration: '',
  },
  executiveSummary: {
    override: '',
  },
};

const RISK_PROFILES = [
  {
    name: 'Conservative',
    description: 'You prioritise capital preservation over growth. You are comfortable with lower returns in exchange for lower volatility and reduced risk of capital loss. Typical growth allocation: 20–30%.',
    growthRange: [0, 0.35],
  },
  {
    name: 'Moderate',
    description: 'You seek a balance between stability and growth, leaning towards more defensive assets. You can tolerate some short-term volatility for moderate long-term returns. Typical growth allocation: 30–50%.',
    growthRange: [0.3, 0.55],
  },
  {
    name: 'Balanced',
    description: 'You seek a balance of growth and income with a diversified portfolio. You accept that returns will vary and some years may produce negative returns. Typical growth allocation: 50–70%.',
    growthRange: [0.45, 0.75],
  },
  {
    name: 'Growth',
    description: 'You prioritise long-term capital growth and are comfortable with higher short-term volatility. You have a longer investment horizon and can ride out market downturns. Typical growth allocation: 70–85%.',
    growthRange: [0.65, 0.90],
  },
  {
    name: 'High Growth',
    description: 'You seek maximum long-term capital growth and are comfortable with significant short-term volatility. You have a long investment horizon and strong capacity to absorb losses. Typical growth allocation: 85–100%.',
    growthRange: [0.80, 1.01],
  },
];

const PAGE_SIZE = 25;

const useSoaBuilder = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [draftId, setDraftId] = useState(null);
  const [draftStatus, setDraftStatus] = useState('draft');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftVersion, setDraftVersion] = useState(1);
  const [drafts, setDrafts] = useState([]);
  const [totalDrafts, setTotalDrafts] = useState(0);
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [showDraftsList, setShowDraftsList] = useState(true);

  // Folder & search/filter state
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = All SOAs
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // null | 'draft' | 'finalised'
  const [ownerFilter, setOwnerFilter] = useState(null); // null | 'mine'
  const [sortBy, setSortBy] = useState('updated');
  const [currentPage, setCurrentPage] = useState(1);

  // Conflict state
  const [editConflict, setEditConflict] = useState(null);

  // Audit log & compliance state
  const [auditLog, setAuditLog] = useState([]);
  const [complianceWarnings, setComplianceWarnings] = useState(null);

  // Adviser profile, client, template state
  const [adviserProfile, setAdviserProfile] = useState(null);
  const [clientId, setClientId] = useState(null);

  const autoSaveTimer = useRef(null);
  const searchTimer = useRef(null);

  // Fetch portfolios on mount
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const data = await soaBuilderAPI.getPortfolios();
        setPortfolios(data);
      } catch (err) {
        console.error('Failed to fetch portfolios:', err);
      }
    };
    fetchPortfolios();
  }, []);

  // Fetch adviser profile on mount
  useEffect(() => {
    const fetchAdviserProfile = async () => {
      try {
        const profile = await soaBuilderAPI.getAdviserProfile();
        if (profile && (profile.adviser_name || profile.adviser_email)) {
          setAdviserProfile(profile);
        }
      } catch (err) {
        console.error('Failed to fetch adviser profile:', err);
      }
    };
    fetchAdviserProfile();
  }, []);

  // Fetch folders
  const fetchFolders = useCallback(async () => {
    try {
      const data = await soaBuilderAPI.listFolders();
      setFolders(data);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    }
  }, []);

  // Fetch drafts with filters
  const fetchDrafts = useCallback(async () => {
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
        sort: sortBy,
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (selectedFolderId === 'unfiled') params.folder_id = 'unfiled';
      else if (selectedFolderId) params.folder_id = selectedFolderId;
      if (ownerFilter) params.owner = ownerFilter;

      const data = await soaBuilderAPI.listDrafts(params);
      setDrafts(data.drafts || []);
      setTotalDrafts(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch drafts:', err);
    }
  }, [currentPage, sortBy, searchQuery, statusFilter, selectedFolderId, ownerFilter]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, selectedFolderId, ownerFilter, sortBy]);

  // Debounced search
  const setSearch = useCallback((q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchQuery(q);
    }, 300);
  }, []);

  // Auto-save debounce
  const triggerAutoSave = useCallback(() => {
    if (!draftId || draftStatus === 'finalised' || editConflict) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        const clientName = formData.clientDetails?.fullName || '';
        const result = await soaBuilderAPI.updateDraft(draftId, {
          form_data: formData,
          client_name: clientName,
          title: draftTitle || `${clientName || 'Untitled'} SOA`,
          portfolio_code: formData.strategy?.portfolioCode || null,
          client_id: clientId || null,
          version: draftVersion,
        });
        setDraftVersion(result.version);
        setLastSaved(new Date());
      } catch (err) {
        if (err.response?.status === 409) {
          // Conflict — another user modified this draft
          setEditConflict(err.response.data);
        } else {
          console.error('Auto-save failed:', err);
        }
      } finally {
        setIsSaving(false);
      }
    }, 1500);
  }, [draftId, draftStatus, formData, draftTitle, draftVersion, editConflict, clientId]);

  // Update form data for a specific section
  const updateFormData = useCallback((section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data },
    }));
  }, []);

  // Trigger auto-save on form data change (after initial draft creation)
  useEffect(() => {
    if (draftId) {
      triggerAutoSave();
    }
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData, draftId, triggerAutoSave]);

  // Step navigation
  const setStep = useCallback((step) => {
    if (step >= 1 && step <= STEP_ORDER.length) {
      setCurrentStep(step);
    }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, STEP_ORDER.length));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  // Create new draft
  const createDraft = useCallback(async (title) => {
    try {
      setIsLoading(true);
      setError(null);

      // Auto-populate adviser details from profile on new draft
      let draftFormData = { ...formData };
      if (adviserProfile) {
        const w = draftFormData.warnings || {};
        if (!w.adviserName && adviserProfile.adviser_name) w.adviserName = adviserProfile.adviser_name;
        if (!w.adviserEmail && adviserProfile.adviser_email) w.adviserEmail = adviserProfile.adviser_email;
        if (!w.adviserPhone && adviserProfile.adviser_phone) w.adviserPhone = adviserProfile.adviser_phone;
        if (!w.afslNumber && adviserProfile.afsl_number) w.afslNumber = adviserProfile.afsl_number;
        if (!w.authorisedRepNumber && adviserProfile.authorised_rep_number) w.authorisedRepNumber = adviserProfile.authorised_rep_number;
        draftFormData = { ...draftFormData, warnings: { ...draftFormData.warnings, ...w } };
      }

      // Auto-populate default templates
      try {
        const defaults = await soaBuilderAPI.getTemplateDefaults();
        if (defaults && Object.keys(defaults).length > 0) {
          for (const [fieldKey, content] of Object.entries(defaults)) {
            const [section, field] = fieldKey.split('.');
            if (section && field && draftFormData[section]) {
              if (!draftFormData[section][field]) {
                draftFormData = {
                  ...draftFormData,
                  [section]: { ...draftFormData[section], [field]: content },
                };
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch template defaults:', err);
      }

      setFormData(draftFormData);

      const clientName = draftFormData.clientDetails?.fullName || '';
      const draft = await soaBuilderAPI.createDraft({
        title: title || `${clientName || 'New'} SOA`,
        form_data: draftFormData,
        client_name: clientName,
        client_id: clientId || null,
      });
      setDraftId(draft.id);
      setDraftStatus(draft.status);
      setDraftTitle(draft.title);
      setDraftVersion(draft.version || 1);
      setLastSaved(new Date());
      setEditConflict(null);
      await fetchDrafts();
      return draft;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [formData, fetchDrafts, adviserProfile, clientId]);

  // Load a draft
  const loadDraft = useCallback(async (id) => {
    try {
      setIsLoading(true);
      setError(null);
      setEditConflict(null);
      const draft = await soaBuilderAPI.getDraft(id);
      setDraftId(draft.id);
      setDraftStatus(draft.status);
      setDraftTitle(draft.title || '');
      setDraftVersion(draft.version || 1);
      if (draft.form_data) {
        // Merge with defaults to ensure all keys exist
        const merged = { ...DEFAULT_FORM_DATA };
        for (const key of Object.keys(DEFAULT_FORM_DATA)) {
          if (draft.form_data[key]) {
            if (typeof DEFAULT_FORM_DATA[key] === 'object' && !Array.isArray(DEFAULT_FORM_DATA[key])) {
              merged[key] = { ...DEFAULT_FORM_DATA[key], ...draft.form_data[key] };
            } else {
              merged[key] = draft.form_data[key];
            }
          }
        }
        // Migrate old drafts: move strategy.retirementProjection to top-level
        if (merged.strategy?.retirementProjection && !draft.form_data.retirementProjection) {
          const oldRp = merged.strategy.retirementProjection;
          // Remove from strategy
          const { retirementProjection: _removed, ...strategyRest } = merged.strategy;
          merged.strategy = strategyRest;
        }
        setFormData(merged);
      }
      setClientId(draft.client_id || null);
      setCurrentStep(1);
      setShowDraftsList(false);

      // Load portfolio details if one was selected
      if (draft.portfolio_code) {
        const portfolio = portfolios.find(p => p.portfolio_code === draft.portfolio_code);
        if (portfolio) setSelectedPortfolio(portfolio);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsLoading(false);
    }
  }, [portfolios]);

  // Delete a draft
  const deleteDraft = useCallback(async (id) => {
    try {
      await soaBuilderAPI.deleteDraft(id);
      if (id === draftId) {
        setDraftId(null);
        setDraftStatus('draft');
        setFormData(DEFAULT_FORM_DATA);
        setDraftTitle('');
        setDraftVersion(1);
        setSelectedPortfolio(null);
        setEditConflict(null);
        setClientId(null);
      }
      await fetchDrafts();
      await fetchFolders();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [draftId, fetchDrafts, fetchFolders]);

  // Finalise
  const finaliseDraft = useCallback(async ({ acknowledgeWarnings = false } = {}) => {
    if (!draftId) return;
    try {
      setIsLoading(true);
      // Save current state first
      const clientName = formData.clientDetails?.fullName || '';
      await soaBuilderAPI.updateDraft(draftId, {
        form_data: formData,
        client_name: clientName,
        title: draftTitle || `${clientName || 'Untitled'} SOA`,
        portfolio_code: formData.strategy?.portfolioCode || null,
        client_id: clientId || null,
        version: draftVersion,
      });
      const result = await soaBuilderAPI.finaliseDraft(draftId, { acknowledgeWarnings });
      setDraftStatus('finalised');
      setDraftVersion(result.version || draftVersion);
      setComplianceWarnings(null);
      await fetchDrafts();
      return result;
    } catch (err) {
      if (err.response?.status === 409) {
        setEditConflict(err.response.data);
      } else if (err.response?.status === 422 && err.response.data?.detail?.warnings) {
        // Compliance warnings — surface them for the modal
        setComplianceWarnings(err.response.data.detail);
        return { complianceBlock: true };
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [draftId, formData, draftTitle, draftVersion, fetchDrafts, clientId]);

  // Export PDF
  const exportPdf = useCallback(async () => {
    try {
      setIsExporting(true);
      setError(null);
      const blob = await soaBuilderAPI.exportPdf({
        form_data: formData,
        portfolio_code: formData.strategy?.portfolioCode || null,
        draft_id: draftId || null,
        draft_version: draftVersion || null,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = formData.clientDetails?.fullName || 'SOA';
      a.download = `${clientName} - Statement of Advice.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  }, [formData, draftId, draftVersion]);

  // Select portfolio and fetch fee data
  const selectPortfolio = useCallback(async (portfolioCode) => {
    const portfolio = portfolios.find(p => p.portfolio_code === portfolioCode);
    setSelectedPortfolio(portfolio || null);

    updateFormData('strategy', { portfolioCode });

    if (portfolioCode) {
      try {
        const feeData = await soaBuilderAPI.getFeeData(portfolioCode);
        if (feeData.icr !== null) {
          updateFormData('fees', { portfolioIcr: feeData.icr.toString() });
        }
      } catch (err) {
        console.error('Failed to fetch fee data:', err);
      }
    }
  }, [portfolios, updateFormData]);

  // Reset to new SOA
  const resetForm = useCallback(async () => {
    let newFormData = { ...DEFAULT_FORM_DATA };

    // Auto-populate adviser details from profile
    if (adviserProfile) {
      newFormData = {
        ...newFormData,
        warnings: {
          ...newFormData.warnings,
          adviserName: adviserProfile.adviser_name || '',
          adviserEmail: adviserProfile.adviser_email || '',
          adviserPhone: adviserProfile.adviser_phone || '',
          afslNumber: adviserProfile.afsl_number || '',
          authorisedRepNumber: adviserProfile.authorised_rep_number || '',
        },
      };
    }

    // Auto-populate default templates
    try {
      const defaults = await soaBuilderAPI.getTemplateDefaults();
      if (defaults && Object.keys(defaults).length > 0) {
        for (const [fieldKey, content] of Object.entries(defaults)) {
          const [section, field] = fieldKey.split('.');
          if (section && field && newFormData[section]) {
            newFormData = {
              ...newFormData,
              [section]: { ...newFormData[section], [field]: content },
            };
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch template defaults:', err);
    }

    setFormData(newFormData);
    setDraftId(null);
    setDraftStatus('draft');
    setDraftTitle('');
    setDraftVersion(1);
    setSelectedPortfolio(null);
    setCurrentStep(1);
    setError(null);
    setLastSaved(null);
    setEditConflict(null);
    setClientId(null);
  }, [adviserProfile]);

  // Filter portfolios by risk profile
  const getFilteredPortfolios = useCallback((profileName) => {
    const profile = RISK_PROFILES.find(p => p.name === profileName);
    if (!profile) return portfolios;
    return portfolios.filter(p => {
      const ge = p.growth_exposure;
      return ge !== null && ge >= profile.growthRange[0] && ge < profile.growthRange[1];
    });
  }, [portfolios]);

  // ── Folder functions ──────────────────────────────────────────────

  const createFolder = useCallback(async (name, parentId = null) => {
    try {
      const folder = await soaBuilderAPI.createFolder(name, parentId);
      await fetchFolders();
      return folder;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      throw err;
    }
  }, [fetchFolders]);

  const renameFolder = useCallback(async (folderId, name) => {
    try {
      await soaBuilderAPI.updateFolder(folderId, { name });
      await fetchFolders();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [fetchFolders]);

  const deleteFolder = useCallback(async (folderId) => {
    try {
      await soaBuilderAPI.deleteFolder(folderId);
      if (selectedFolderId === folderId) setSelectedFolderId(null);
      await fetchFolders();
      await fetchDrafts();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [selectedFolderId, fetchFolders, fetchDrafts]);

  // ── Duplicate & Move ──────────────────────────────────────────────

  const duplicateDraft = useCallback(async (id) => {
    try {
      const newDraft = await soaBuilderAPI.duplicateDraft(id);
      await fetchDrafts();
      await fetchFolders();
      return newDraft;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [fetchDrafts, fetchFolders]);

  const moveDraft = useCallback(async (id, folderId) => {
    try {
      await soaBuilderAPI.moveDraft(id, folderId);
      await fetchDrafts();
      await fetchFolders();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [fetchDrafts, fetchFolders]);

  // ── Sharing & privacy ──────────────────────────────────────────────

  const togglePrivate = useCallback(async (id, currentlyPrivate) => {
    try {
      await soaBuilderAPI.updateDraft(id, { is_private: !currentlyPrivate }, { force: true });
      await fetchDrafts();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [fetchDrafts]);

  const fetchDomainUsers = useCallback(async () => {
    try {
      return await soaBuilderAPI.listDomainUsers();
    } catch (err) {
      console.error('Failed to fetch domain users:', err);
      return [];
    }
  }, []);

  const fetchDraftShares = useCallback(async (draftId) => {
    try {
      return await soaBuilderAPI.listDraftShares(draftId);
    } catch (err) {
      console.error('Failed to fetch draft shares:', err);
      return [];
    }
  }, []);

  const shareDraft = useCallback(async (draftId, userEmails) => {
    try {
      const result = await soaBuilderAPI.shareDraft(draftId, userEmails);
      await fetchDrafts();
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [fetchDrafts]);

  const unshareDraft = useCallback(async (draftId, userEmail) => {
    try {
      await soaBuilderAPI.unshareDraft(draftId, userEmail);
      await fetchDrafts();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [fetchDrafts]);

  // ── Client functions ────────────────────────────────────────────────

  const loadClientIntoForm = useCallback((client) => {
    setClientId(client.id);
    updateFormData('clientDetails', {
      fullName: client.full_name || '',
      dateOfBirth: client.date_of_birth || '',
      address: client.address || '',
      email: client.email || '',
      phone: client.phone || '',
      employment: client.employment || '',
      employer: client.employer || '',
      homeowner: client.homeowner || 'Yes',
      isCouple: client.is_couple || false,
      partnerName: client.partner_name || '',
      partnerDob: client.partner_dob || '',
      partnerEmployment: client.partner_employment || '',
      dependants: client.dependants || '',
    });
  }, [updateFormData]);

  const saveAsClient = useCallback(async () => {
    const cd = formData.clientDetails;
    if (!cd?.fullName?.trim()) return;

    const clientData = {
      full_name: cd.fullName,
      date_of_birth: cd.dateOfBirth || null,
      address: cd.address || null,
      email: cd.email || null,
      phone: cd.phone || null,
      employment: cd.employment || null,
      employer: cd.employer || null,
      homeowner: cd.homeowner || 'Yes',
      is_couple: cd.isCouple || false,
      partner_name: cd.partnerName || null,
      partner_dob: cd.partnerDob || null,
      partner_employment: cd.partnerEmployment || null,
      dependants: cd.dependants || null,
    };

    try {
      let client;
      if (clientId) {
        client = await soaBuilderAPI.updateClient(clientId, clientData);
      } else {
        client = await soaBuilderAPI.createClient(clientData);
      }
      setClientId(client.id);
      return client;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      throw err;
    }
  }, [formData.clientDetails, clientId]);

  // ── Adviser profile functions ─────────────────────────────────────

  const useAdviserProfile = useCallback(() => {
    if (!adviserProfile) return;
    updateFormData('warnings', {
      adviserName: adviserProfile.adviser_name || '',
      adviserEmail: adviserProfile.adviser_email || '',
      adviserPhone: adviserProfile.adviser_phone || '',
      afslNumber: adviserProfile.afsl_number || '',
      authorisedRepNumber: adviserProfile.authorised_rep_number || '',
    });
  }, [adviserProfile, updateFormData]);

  const saveAdviserProfile = useCallback(async () => {
    const w = formData.warnings || {};
    const profileData = {
      adviser_name: w.adviserName || null,
      adviser_email: w.adviserEmail || null,
      adviser_phone: w.adviserPhone || null,
      afsl_number: w.afslNumber || null,
      authorised_rep_number: w.authorisedRepNumber || null,
    };
    try {
      const saved = await soaBuilderAPI.saveAdviserProfile(profileData);
      setAdviserProfile(saved);
      return saved;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      throw err;
    }
  }, [formData.warnings]);

  // ── Conflict resolution ───────────────────────────────────────────

  const reloadLatest = useCallback(async () => {
    if (!draftId) return;
    await loadDraft(draftId);
    setEditConflict(null);
  }, [draftId, loadDraft]);

  const forceSave = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsSaving(true);
      const clientName = formData.clientDetails?.fullName || '';
      const result = await soaBuilderAPI.updateDraft(draftId, {
        form_data: formData,
        client_name: clientName,
        title: draftTitle || `${clientName || 'Untitled'} SOA`,
        portfolio_code: formData.strategy?.portfolioCode || null,
        client_id: clientId || null,
        version: draftVersion,
      }, { force: true });
      setDraftVersion(result.version);
      setEditConflict(null);
      setLastSaved(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsSaving(false);
    }
  }, [draftId, formData, draftTitle, draftVersion, clientId]);

  // ── Sub-Plan A: Blend functions ─────────────────────────────────────

  const calculateBlend = useCallback(async () => {
    try {
      const strategy = formData.strategy || {};
      const result = await soaBuilderAPI.calculateBlend({
        model_weights: strategy.modelWeights || {},
        fund_entries: (strategy.fundEntries || []).map(f => ({
          tr_code: f.tr_code,
          fund_name: f.fund_name,
          asset_class: f.asset_class,
          sub_asset_class: f.sub_asset_class,
          blender_asset_class: f.blender_asset_class,
          weight: parseFloat(f.weight) || 0,
          ter: f.ter,
        })),
        asset_class_weights: strategy.assetClassWeights || {},
      });
      updateFormData('strategy', {
        blendResults: result,
        portfolioWeights: { ...(strategy.modelWeights || {}), ...(strategy.assetClassWeights || {}) },
      });
      // Update ICR from blend
      if (result.icr != null) {
        updateFormData('fees', { portfolioIcr: result.icr.toFixed(4) });
      }
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Blend calculation failed');
      throw err;
    }
  }, [formData.strategy, updateFormData]);

  const searchFunds = useCallback(async (query) => {
    return await soaBuilderAPI.searchFunds(query);
  }, []);

  const fetchAssetClasses = useCallback(async () => {
    return await soaBuilderAPI.getAssetClasses();
  }, []);

  // ── Audit log ───────────────────────────────────────────────────────

  const getAuditLog = useCallback(async (id = null) => {
    const targetId = id || draftId;
    if (!targetId) return [];
    try {
      const logs = await soaBuilderAPI.fetchAuditLog(targetId);
      setAuditLog(logs);
      return logs;
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      return [];
    }
  }, [draftId]);

  // ── Compliance check ────────────────────────────────────────────────

  const runComplianceCheck = useCallback(async () => {
    if (!draftId) return null;
    try {
      const result = await soaBuilderAPI.checkCompliance(draftId);
      setComplianceWarnings(result.passed ? null : result);
      return result;
    } catch (err) {
      console.error('Compliance check failed:', err);
      return null;
    }
  }, [draftId]);

  return {
    // Step navigation
    currentStep,
    setStep,
    nextStep,
    prevStep,
    stepDefs: STEP_DEFS,
    stepOrder: STEP_ORDER,

    // Form data
    formData,
    updateFormData,
    draftTitle,
    setDraftTitle,

    // Draft management
    draftId,
    draftStatus,
    draftVersion,
    drafts,
    totalDrafts,
    createDraft,
    loadDraft,
    deleteDraft,
    finaliseDraft,
    resetForm,
    fetchDrafts,
    showDraftsList,
    setShowDraftsList,

    // Folders
    folders,
    selectedFolderId,
    setSelectedFolderId: (id) => { setSelectedFolderId(id); },
    createFolder,
    renameFolder,
    deleteFolder,

    // Search & filter
    searchQuery,
    setSearch,
    statusFilter,
    setStatusFilter,
    ownerFilter,
    setOwnerFilter,
    sortBy,
    setSortBy,
    currentPage,
    setCurrentPage,
    pageSize: PAGE_SIZE,

    // Duplicate & move
    duplicateDraft,
    moveDraft,

    // Sharing & privacy
    togglePrivate,
    fetchDomainUsers,
    fetchDraftShares,
    shareDraft,
    unshareDraft,

    // Conflict
    editConflict,
    reloadLatest,
    forceSave,

    // Portfolio
    portfolios,
    selectedPortfolio,
    selectPortfolio,
    getFilteredPortfolios,
    riskProfiles: RISK_PROFILES,

    // Blend (Sub-Plan A)
    calculateBlend,
    searchFunds,
    fetchAssetClasses,

    // Audit log
    auditLog,
    getAuditLog,

    // Compliance
    complianceWarnings,
    setComplianceWarnings,
    runComplianceCheck,

    // PDF
    exportPdf,
    isExporting,

    // Adviser profile
    adviserProfile,
    useAdviserProfile,
    saveAdviserProfile,

    // Client registry
    clientId,
    loadClientIntoForm,
    saveAsClient,

    // State
    isSaving,
    isLoading,
    error,
    setError,
    lastSaved,
    isFinalised: draftStatus === 'finalised',
  };
};

export { useSoaBuilder, RISK_PROFILES, STEP_ORDER, STEP_DEFS, DEFAULT_FORM_DATA };
export default useSoaBuilder;
